import { isRelationshipToVeteran } from '../helpers';

export function transformProfileAddress(addr) {
  if (!addr) return undefined;
  // Legacy address (yourAddress) is already in the submit shape
  if (addr.street) {
    const legacy = { ...addr };
    // street2 has a minLength of 1 in vets-json-schema; drop it when empty
    if (!legacy.street2) delete legacy.street2;
    return legacy;
  }
  const isMilitary = addr.isMilitary ?? addr.addressType === 'MILITARY';
  const transformed = {
    isMilitary,
    street: addr.addressLine1 || '',
    city: addr.city || '',
    state:
      addr.addressType === 'INTERNATIONAL'
        ? addr.province || ''
        : addr.stateCode || '',
    postalCode:
      addr.addressType === 'INTERNATIONAL'
        ? addr.internationalPostalCode || ''
        : addr.zipCode || '',
    country: addr.countryCodeIso3 || addr.countryName || 'USA',
  };
  // street2 has a minLength of 1 in vets-json-schema; only include when present
  if (addr.addressLine2) {
    transformed.street2 = addr.addressLine2;
  }
  return transformed;
}

export function transformProfilePhone(phone) {
  if (!phone) return undefined;
  if (typeof phone === 'string') {
    return {
      countryCode: 'US',
      contact: phone,
    };
  }
  if (phone.contact) {
    const cleaned = {
      countryCode: phone.countryCode || 'US',
      contact: phone.contact,
    };
    if (phone.callingCode) {
      cleaned.callingCode = phone.callingCode;
    }
    return cleaned;
  }
  const { areaCode = '', phoneNumber = '', countryCode = '1' } = phone;
  if (phoneNumber) {
    const fullNumber = `${areaCode}${phoneNumber}`;
    return {
      countryCode: countryCode === '1' ? 'US' : countryCode,
      contact: fullNumber,
    };
  }
  return undefined;
}

export function transformProfileEmail(email) {
  if (!email) return undefined;
  if (typeof email === 'string') return email;
  return email.emailAddress;
}

export function transformClaimant(formData) {
  const parsedFormData = JSON.parse(formData);
  const transformedValue = { ...parsedFormData };

  const contactInfo =
    parsedFormData.yourContactInformation || parsedFormData.contactInformation;

  // TODO(phase 3): keep the flat-key fallback (yourAddress/yourEmail/yourPhone)
  // until legacy-key SiP forms expire (~60 days after enablement)
  const normalizedAddress = contactInfo?.mailingAddress
    ? transformProfileAddress(contactInfo.mailingAddress)
    : transformProfileAddress(parsedFormData.yourAddress);

  const normalizedEmail = contactInfo?.email
    ? transformProfileEmail(contactInfo.email)
    : transformProfileEmail(parsedFormData.yourEmail);

  const normalizedPhone = contactInfo
    ? transformProfilePhone(
        contactInfo?.mobilePhone?.phoneNumber
          ? contactInfo?.mobilePhone
          : contactInfo?.homePhone,
      )
    : transformProfilePhone(parsedFormData.yourPhone);

  const normalizedName = parsedFormData.yourName || parsedFormData.fullName;

  const normalizedDob =
    parsedFormData.yourDateOfBirth || parsedFormData.dateOfBirth;

  const normalizedSsn =
    parsedFormData.yourSocialSecurityNumber || parsedFormData.ssn;

  // If custodian, your* fields become filingCustodian* fields, and child* fields become claimant* fields.
  // Otherwise, your* fields become claimant* fields.
  if (isRelationshipToVeteran(parsedFormData) === 'custodian') {
    transformedValue.claimantFullName = parsedFormData.childFullName;
    transformedValue.claimantDateOfBirth = parsedFormData.childDateOfBirth;
    transformedValue.claimantAddress = parsedFormData.childAddress;
    transformedValue.claimantEmail = parsedFormData.childEmail;
    transformedValue.claimantPhone = parsedFormData.childPhone;
    transformedValue.claimantIsVeteran = false;
    transformedValue.claimantSocialSecurityNumber =
      parsedFormData.childSocialSecurityNumber;

    transformedValue.filingCustodianFullName = normalizedName;
    transformedValue.filingCustodianAddress = normalizedAddress;
    transformedValue.filingCustodianEmail = normalizedEmail;
    transformedValue.filingCustodianPhone = normalizedPhone;
  } else {
    transformedValue.claimantFullName = normalizedName;
    transformedValue.claimantDateOfBirth = normalizedDob;
    transformedValue.claimantAddress = normalizedAddress;
    transformedValue.claimantEmail = normalizedEmail;
    transformedValue.claimantPhone = normalizedPhone;
    transformedValue.claimantIsVeteran = parsedFormData.yourVeteran;
    transformedValue.claimantSocialSecurityNumber = normalizedSsn;
  }

  return JSON.stringify(transformedValue);
}
